/**
 * BulkSetRateDialog — set ONE band's rate for MANY selected clients
 * (founder feedback 2026-07-10: "there should be a way to select clients
 * and bulk add rates / change rates at the top of this page").
 *
 * The SetRateDialog shape (same fields and words, same plain-words
 * validation, same canonical 2-decimal money string) plus an explicit
 * band pick, applied through the EXISTING per-client PUT, one client at
 * a time (sequential: the server stays the single validator per write;
 * no new endpoint). "Set rate for {n} clients" is disabled only while
 * running: pressed with a gap it sends nothing, shows every gap's
 * sentence under its box and moves focus to the first. Progress shows n
 * of N while it runs; failures are COLLECTED and named per client on
 * labelled lines — a partial apply is never silent. The caller refetches
 * the aggregate afterwards whether or not everything landed.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { Field } from '@/components/primitives/Field';
import { FLD_MONEY, FLD_MONEY_SIGN } from '@/components/primitives/modalFormStyles';
import { ApiError } from '@/lib/api';
import { contactSentence, saveFailureText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { RateBandOption } from '@/lib/notes';
import { canonicalRateInput, putClientRate, rateExample, rateInputProblem } from '@/lib/clientRates';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

/** The per-client fact-shaped save errors, plain words (SetRateDialog's map). */
function saveErrorMessage(err: unknown, contact: SupportContact, band: string): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return contactSentence("Setting rates isn't part of your access. Ask {contact_billing} to set them.", contact);
    }
    if (err.status === 422) return "That rate band isn't offered any more. Choose one from the list.";
    if (err.status === 400) return `Enter the amount as dollars and cents, with no $ sign: ${rateExample(band)}. Then pick a start date.`;
  }
  return (
    saveFailureText(err, contact) ??
    contactSentence(
      "The rates didn't save. Check the amount and the date, and if they still won't save, tell {contact_system}.",
      contact,
    )
  );
}

export interface BulkSetRateClient {
  id: string;
  display_name: string;
}

export interface BulkSetRateDialogProps {
  /** The configured band vocabulary (the aggregate response's columns). */
  bands: readonly RateBandOption[];
  /** The selected clients, in the grid's order. */
  clients: readonly BulkSetRateClient[];
  today: string;
  /** Called once the run finishes (fully OR partially) — the grid
   *  refetches either way, so every landed rate shows. */
  onApplied: () => void;
  onClose: () => void;
}

type RunState =
  | { phase: 'editing' }
  | { phase: 'running'; done: number }
  | { phase: 'done'; failures: Array<{ name: string; message: string }> };

export function BulkSetRateDialog({
  bands,
  clients,
  today,
  onApplied,
  onClose,
}: BulkSetRateDialogProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const contact = useSupportContact();
  const [band, setBand] = useState('');
  const [rate, setRate] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [run, setRun] = useState<RunState>({ phase: 'editing' });
  const [bandGap, setBandGap] = useState(false);
  /** The amount's problem shows once the box is left or the primary is pressed. */
  const [problemShown, setProblemShown] = useState(false);
  const [dateGap, setDateGap] = useState(false);
  const bandRef = useRef<HTMLSelectElement | null>(null);
  const amountRef = useRef<HTMLInputElement | null>(null);
  const dateRef = useRef<HTMLInputElement | null>(null);

  const problem = rateInputProblem(rate, band);
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom);
  const showProblem = problemShown && problem !== null;

  async function apply(): Promise<void> {
    const canonical = canonicalRateInput(rate);
    const failures: Array<{ name: string; message: string }> = [];
    setRun({ phase: 'running', done: 0 });
    // Sequential on purpose: one write at a time keeps the server the
    // single validator and the progress count honest.
    for (let i = 0; i < clients.length; i++) {
      const client = clients[i];
      try {
        await putClientRate(tenantSlug, client.id, {
          rate_band: band,
          rate: canonical,
          effective_from: effectiveFrom,
        });
      } catch (err) {
        failures.push({ name: client.display_name, message: saveErrorMessage(err, contact, band) });
      }
      setRun({ phase: 'running', done: i + 1 });
    }
    // Refetch regardless — the rates that landed must show.
    onApplied();
    if (failures.length === 0) {
      onClose();
      return;
    }
    setRun({ phase: 'done', failures });
  }

  function pressSet(): void {
    const noBand = band === '';
    if (noBand || problem !== null || !dateOk) {
      setBandGap(noBand);
      setProblemShown(true);
      setDateGap(!dateOk);
      (noBand ? bandRef : problem !== null ? amountRef : dateRef).current?.focus();
      return;
    }
    void apply();
  }

  const running = run.phase === 'running';
  const failures = run.phase === 'done' ? run.failures : [];
  const succeeded = clients.length - failures.length;
  const count = `${clients.length} ${clients.length === 1 ? 'client' : 'clients'}`;

  return (
    <Dialog
      title={`Set a rate for ${count}`}
      size="sm"
      testId="bulk-rate-dialog"
      onClose={running ? () => undefined : onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={running}>
            {run.phase === 'done' ? 'Close' : 'Cancel'}
          </Button>
          {run.phase !== 'done' && (
            <Button
              variant="primary"
              size="sm"
              disabled={running}
              loading={running}
              data-testid="bulk-rate-save"
              onClick={pressSet}
            >
              Set rate for {count}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-2 py-2">
        <Field label="Rate band" required htmlFor="bulk-rate-band" error={bandGap ? 'Choose a rate band.' : undefined}>
          <SelectField size="field"
            ref={bandRef}
            id="bulk-rate-band"
            data-testid="bulk-rate-band"
            className="w-full"
            value={band}
            disabled={running || run.phase === 'done'}
            aria-required="true"
            aria-invalid={bandGap ? 'true' : undefined}
            aria-describedby={bandGap ? 'bulk-rate-band-error' : undefined}
            onChange={(e) => {
              setBandGap(false);
              setBand(e.target.value);
            }}
          >
            <option value="">Choose a band</option>
            {bands.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field
          label="Hourly rate"
          required
          htmlFor="bulk-rate-amount"
          help={`Dollars and cents, for example ${rateExample(band)}.`}
        >
          <div className="relative">
            <span aria-hidden="true" className={FLD_MONEY_SIGN}>
              $
            </span>
            <input
              ref={amountRef}
              id="bulk-rate-amount"
              data-testid="bulk-rate-amount"
              type="text"
              inputMode="decimal"
              className={FLD_MONEY}
              value={rate}
              disabled={running || run.phase === 'done'}
              aria-required="true"
              aria-invalid={showProblem ? 'true' : undefined}
              aria-describedby={showProblem ? 'bulk-rate-amount-help bulk-rate-amount-error' : 'bulk-rate-amount-help'}
              onBlur={() => setProblemShown(true)}
              onChange={(e) => {
                setProblemShown(false);
                setRate(e.target.value);
              }}
            />
          </div>
        </Field>
        {showProblem && (
          <p id="bulk-rate-amount-error" role="alert" data-testid="bulk-rate-problem" className="text-xs text-pepper">
            {problem}
          </p>
        )}
        <Field
          label="Starts on"
          required
          htmlFor="bulk-rate-from"
          help="Time before this date keeps its old rate."
          error={dateGap ? 'Choose the date this rate starts.' : undefined}
        >
          <DateInput
            ref={dateRef}
            id="bulk-rate-from"
            data-testid="bulk-rate-from"
            size="field" className="w-full max-w-[11rem]"
            value={effectiveFrom}
            disabled={running || run.phase === 'done'}
            aria-required="true"
            aria-invalid={dateGap ? 'true' : undefined}
            aria-describedby={dateGap ? 'bulk-rate-from-help bulk-rate-from-error' : 'bulk-rate-from-help'}
            onChange={(e) => {
              setDateGap(false);
              setEffectiveFrom(e.target.value);
            }}
          />
        </Field>

        {running && (
          <p
            role="status"
            data-testid="bulk-rate-progress"
            className="text-xs text-ink-soft"
          >
            Setting rate {Math.min(run.done + 1, clients.length)} of {clients.length}
          </p>
        )}

        {run.phase === 'done' && (
          <div role="alert" data-testid="bulk-rate-failures" className="text-xs">
            <p className="text-ink">
              Set for {succeeded} of {clients.length} clients. The ones below didn't save, so fix
              each reason and set them again.
            </p>
            <dl className="mt-1 grid gap-1">
              {failures.map((f, i) => (
                <div key={`${f.name}-${i}`} className="flex flex-wrap items-baseline gap-x-3">
                  <dt className="shrink-0 min-w-[9.5em] text-label tracking-label font-semibold uppercase text-sage-400">
                    {f.name}
                  </dt>
                  <dd className="m-0 min-w-0 flex-1 basis-[9em] text-value font-medium text-pepper [overflow-wrap:anywhere]">
                    {f.message}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </Dialog>
  );
}
